import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as React from 'react';

// material-ui
import { useTheme } from "@mui/material/styles";
import { Box, Toolbar, useMediaQuery, Typography, Backdrop, CircularProgress,} from "@mui/material";

// project import
import Drawer from "./drawer/Drawer";
import Header from "./header/Header";

// types
import { openDrawer, setPasswordReset } from "../../store/slices/applicationSlice";

import { bloqcibeApi, useGetSponsorDetailsQuery, useGetMeUserQuery, useGetUserContactsQuery, useGetFianceAdminByIdQuery } from "../../store/slices/apiSlice";
import ShowMessage from "../../components/showMessage/ShowMessage";
import { WebSocketProvider } from "../../hooks/WebsocketProvider";
import { setSubjectLoggedIn } from "../../store/slices/authSlice";
import ResetPasswordDialog from "../../components/common/resetPasswordDialog";
import ProfileSetup from "../../pages/profileSetup/ProfileSetup"
import AnimatedBloqcubeLogo from "../../components/Logo/AnimatedBloqcubeLogo";
import { getUserRole } from "../../pages/util";
import ExpiredDialog from "../../components/common/ExpiredSessionDialog";
import moment from "moment";
import Chats from "../../components/common/chats/Chats";

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const {data:_userData} = useGetMeUserQuery()
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
  // Token-based user (primary)
  const {user, userData} = useSelector((state) => state.auth);
  
  // Prioritize getMe sponsorId, fallback to token
  const sponsorId = userData?.sponsorId || user?.details?.[0]?.[0]?.sponsorId;
  const { data } = useGetSponsorDetailsQuery(sponsorId, {skip: !sponsorId});
  const { data: data2, isLoading: isLoading2 } = useGetFianceAdminByIdQuery(sponsorId, {skip: !sponsorId});
  const drawerOpen = useSelector((state) => state.application.drawerOpen);

  // getUserRole will check getMe first, then fallback to token
  const userRole = getUserRole(user, null, null, userData);

  const [openDialog, setOpenDialog] = React.useState(false);

  const sessionExpiredDialog = useSelector((state) => state.application.sessionExpiredDialog);

  const {data:userContacts} = useGetUserContactsQuery()
  const [getSubjectDashboardDetail] =
    bloqcibeApi.endpoints.getSubjectDashboardDetail.useLazyQuery();

  // backdrop toggler
  const backdropLoading = useSelector(state => state.application.backdropLoading)

  // drawer toggler

  const handlePasswordDialogClose = () => {
    setOpenDialog(false);
    // Store the close timestamp in localStorage preferences (user-scoped)
    const userId = userData?.id;
    if (userId) {
      const preferences = JSON.parse(localStorage.getItem('preferences') || '{}');
      preferences[`profileSetupDialogClosedAt_${userId}`] = new Date().toISOString();
      localStorage.setItem('preferences', JSON.stringify(preferences));
    }
  }

  const [open, setOpen] = useState(drawerOpen);

  const [isExpired, setIsExpired] = useState(false);
  const [message, setMessage] = useState('');


  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  useEffect(()=>{
    if(sessionExpiredDialog){
      setIsExpired(true);
      setMessage("Your session is expired. Please Login again.")
    }
    if(userRole===''){
      setIsExpired(true);
      setMessage("You have no assigned trials. Please contact the sponsor admin.")
    }
  },[sessionExpiredDialog,userRole])

  useEffect(() => {
    // Check if subject: prioritize getMe, fallback to token
    const isSubject = userData?.userRoles?.some(role => role.role === 'subject') || 
                      (user && user.details?.[0]?.[0]?.['role'] == 'subject');
    const userId = user?.details?.[0]?.[0]?.['userId'] || userData?.id;
    
    if (user && isSubject && userId) {
      getSubjectDashboardDetail(userId).then(response => {
        if(response?.data?.hcDetail?.status === 'Withdrawal' || response?.data?.icDetail?.status === 'Withdrawal'){
          setIsExpired(true); 
          setMessage(`You have been withdrawn from the trial - ${response?.data?.trialDetail.trialTitle}.`)
        }
        if(response?.data?.hcDetail?.status === 'Enrollment_Failure' || response?.data?.icDetail?.status === 'Enrollment_Failure'){
          setIsExpired(true); 
          setMessage(`You have been removed from the trail - ${response?.data?.trialDetail.trialTitle} due to a screening failure.`)
        }
      });
      dispatch(setSubjectLoggedIn(true));
    }
  }, [user]);
  
  // set media wise responsive drawer
  // useEffect(() => {
    // setOpen(!matchDownLG);
    // dispatch(openDrawer({ drawerOpen: !matchDownLG }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [matchDownLG]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  useEffect(() => {
    const now = moment();
    const userId = userData?.id;

    // Early return if no userData
    if (!userData) {
      return;
    }

    // Helper function to check if password reset dialog was recently dismissed (session-based cooldown)
    const wasDialogRecentlyDismissed = () => {
      if (!userId) return false;
      const preferences = JSON.parse(localStorage.getItem('preferences') || '{}');
      const dismissedAt = preferences[`passwordResetDialogDismissedAt_${userId}`];
      if (dismissedAt) {
        const dismissedTime = moment(dismissedAt);
        const hoursSinceDismissed = now.diff(dismissedTime, 'hours');
        // Don't show again for 4 hours if dismissed (for cancellable dialogs only)
        return hoursSinceDismissed < 4;
      }
      return false;
    };

    // Guard: Check if passwordExpiry is valid
    const hasValidExpiry = userData?.passwordExpiry && moment(userData.passwordExpiry).isValid();
    
    // Calculate expiry-related values only if we have valid expiry
    const passwordExpiry = hasValidExpiry ? moment(userData.passwordExpiry) : null;
    const sevenDaysBeforeExpiry = passwordExpiry ? passwordExpiry.clone().subtract(7, 'days') : null;

    // Helper to get hours remaining (more accurate than days for near-expiry)
    const getHoursRemaining = () => {
      if (!passwordExpiry) return null;
      return passwordExpiry.diff(now, 'hours');
    };

    // Helper to format expiry time with timezone offset (z requires moment-timezone plugin, use Z instead)
    const formatExpiryTime = () => {
      if (!passwordExpiry) return '';
      return passwordExpiry.format('hh:mm A');
    };

    // Close password reset dialog by default when handling profile setup
    const closePasswordResetDialog = () => {
      dispatch(setPasswordReset({ openDialog: false }));
    };

    // CASE 1: Temp password + unverified email/phone → Show profile setup dialog
    if (userData?.tempPassword && (!userData?.isEmailVerified || !userData?.isPhoneVerified)) {
      // Ensure password reset dialog is closed when profile setup is shown
      closePasswordResetDialog();
      
      // Check if profile setup dialog was closed and if 24 hours have passed (user-scoped)
      const preferences = JSON.parse(localStorage.getItem('preferences') || '{}');
      const closedAt = userId ? preferences[`profileSetupDialogClosedAt_${userId}`] : null;
      
      if (closedAt) {
        const closedTime = moment(closedAt);
        const hoursSinceClosed = now.diff(closedTime, 'hours');
        
        // Only show dialog if 24 hours have passed since it was closed
        if (hoursSinceClosed >= 24) {
          setOpenDialog(true);
          // Clear the closed timestamp since we're showing it again
          delete preferences[`profileSetupDialogClosedAt_${userId}`];
          localStorage.setItem('preferences', JSON.stringify(preferences));
        } else {
          setOpenDialog(false);
        }
      } else {
        // Dialog hasn't been closed before, show it
        setOpenDialog(true);
      }
      return; // Exit early - profile setup takes priority
    }

    // Close profile setup dialog if we're past the profile setup case
    setOpenDialog(false);

    // CASE 2: Temp password expired → Non-cancellable, contact admin
    if (userData?.tempPassword && hasValidExpiry && now.isAfter(passwordExpiry)) {
      dispatch(
        setPasswordReset({
          openDialog: true,
          formLabel: 'Your temporary password has expired. Please contact your administrator to receive a new temporary password.',
          isCancellable: false,
          isTempPassword: true,
        })
      );
      return;
    }

    // CASE 3: Temp password valid + verified → Show reset dialog (cancellable with cooldown)
    if (
      userData?.tempPassword &&
      userData?.isEmailVerified &&
      userData?.isPhoneVerified
    ) {
      // Check if within 7 days of expiry for temp password (if expiry exists)
      const hoursRemaining = getHoursRemaining();
      let formLabel = 'You are using a temporary password. Please reset your password to continue using the system.';
      let isCancellable = true;

      if (hasValidExpiry && hoursRemaining !== null) {
        const expiryDateTime = passwordExpiry.format('MMM D [at] hh:mm A');
        
        if (hoursRemaining <= 0) {
          // Already handled in CASE 2, but safety check
          formLabel = 'Your temporary password has expired. Please contact your administrator to receive a new temporary password.';
          isCancellable = false;
        } else if (hoursRemaining <= 24) {
          // Expiring within 24 hours - show exact date and time
          formLabel = `Your temporary password will expire in ${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'} on ${expiryDateTime}. Please reset your password immediately.`;
          isCancellable = true;
        } else if (hoursRemaining <= 48) {
          // 1-2 days remaining
          formLabel = `Your temporary password will expire on ${expiryDateTime}. Please reset your password as soon as possible.`;
          isCancellable = true;
        } else if (hoursRemaining <= 168) {
          // Within 7 days
          const daysRemaining = Math.ceil(hoursRemaining / 24);
          formLabel = `Your temporary password will expire in ${daysRemaining} days on ${expiryDateTime}. Please reset your password.`;
          isCancellable = true;
        }
      }

      // Respect cooldown for cancellable dialogs
      if (isCancellable && wasDialogRecentlyDismissed()) {
        dispatch(setPasswordReset({ openDialog: false }));
        return;
      }

      dispatch(
        setPasswordReset({
          openDialog: true,
          formLabel,
          isCancellable,
          isTempPassword: true,
        })
      );
      return;
    }

    // From here on, we handle regular (non-temp) passwords
    // Guard: If no valid expiry date, no dialogs needed
    if (!hasValidExpiry) {
      dispatch(setPasswordReset({ openDialog: false }));
      return;
    }

    // CASE 4: Regular password is already expired → Non-cancellable
    if (now.isAfter(passwordExpiry)) {
      dispatch(
        setPasswordReset({
          openDialog: true,
          formLabel: 'Your password has expired. You will be unable to continue until you reset your password.',
          isCancellable: false,
          isTempPassword: false,
        })
      );
      return;
    }

    // CASE 5: Within 7 days of expiry → Show warning (cancellable with cooldown)
    if (sevenDaysBeforeExpiry && now.isSameOrAfter(sevenDaysBeforeExpiry) && now.isBefore(passwordExpiry)) {
      const hoursRemaining = getHoursRemaining();
      let formLabel = '';

      // Format expiry date and time for display
      const expiryDateTime = passwordExpiry.format('MMM D [at] hh:mm A');
      
      if (hoursRemaining !== null && hoursRemaining <= 24) {
        if (hoursRemaining <= 1) {
          formLabel = `Your password will expire within the hour on ${expiryDateTime}. Please reset it immediately.`;
        } else {
          formLabel = `Your password will expire in ${hoursRemaining} hours on ${expiryDateTime}. Please reset it immediately.`;
        }
      } else if (hoursRemaining !== null && hoursRemaining <= 48) {
        // 1-2 days remaining - show date for clarity
        formLabel = `Your password will expire on ${expiryDateTime}. Please reset it as soon as possible.`;
      } else {
        // 2-7 days remaining
        const daysRemaining = Math.ceil((hoursRemaining || 0) / 24);
        formLabel = `Your password will expire in ${daysRemaining} days on ${expiryDateTime}. Please reset it soon.`;
      }

      // Respect cooldown for cancellable dialogs
      if (wasDialogRecentlyDismissed()) {
        dispatch(setPasswordReset({ openDialog: false }));
        return;
      }

      dispatch(
        setPasswordReset({
          openDialog: true,
          formLabel,
          isCancellable: true,
          isTempPassword: false,
        })
      );
      return;
    }

    // CASE 6: Password is valid and not expiring soon → No dialog needed
    dispatch(setPasswordReset({ openDialog: false }));

  }, [userData, dispatch])

  return (
    <WebSocketProvider>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          '@media (max-width:600px)': {
            backgroundColor: '#F1F2F5',
          },
        }}
      >
        <Header open={open} handleDrawerToggle={handleDrawerToggle} />
        <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />
        <Box
          component="main"
          sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FAFAFA',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box>
            <Toolbar />
            <Outlet />
          </Box>
          <Box sx={{ marginTop: 'auto' }}>
            <Box height={10} sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
              <Typography variant="caption" color={theme.palette.grey[500]}>
                Copyright &copy; Bloqcube 2023. All rights reserved.
              </Typography>
            </Box>
          </Box>
          <ShowMessage />
        </Box>
      </Box>
      {(userRole !== 'sponsor' || userRole !== 'system_admin') && <Chats />}
      <ExpiredDialog isOpen={isExpired} message={message} />

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropLoading}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ height: 150, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <AnimatedBloqcubeLogo />
          </Box>
          <Typography variant="subtitle2" color="inherit">
            Please wait, We are loading your data...
          </Typography>
        </Box>
      </Backdrop>

      {!isExpired && <ResetPasswordDialog />}

      {!isExpired && openDialog && (
        <ProfileSetup
          userData={userData}
          openDialog={openDialog}
          handlePasswordDialogClose={handlePasswordDialogClose}
        />
      )}
    </WebSocketProvider>
  );
};

export default MainLayout;
